/**
 * Edition planner. Turns the editions archive and the timezone table
 * into a print/web schedule. The printer follows this; the ferry
 * follows the tide; the two schedules have never matched, which is
 * fine, because the archive reads the printer's, and the web reads
 * nothing, being a page.
 */

import { EDITIONS_ARCHIVE, type EditionRow } from "../decoys/gen/editions-archive";
import { UNIVERSE_TZ, type UniverseTz } from "../decoys/gen/universe-tz";
import { TELEMETRY_EVENTS } from "../decoys/gen/telemetry-events";

export interface PlannedEdition {
  universe: string;
  edition: string;
  paper: string;
  editor: string;
  status: EditionRow["status"];
  webWindow: string;
  printWindow: string;
  conflict: boolean;
}

/** Minutes-of-day the web edition "lands", expressed in universe-local time. */
const WEB_WINDOWS = ["06:00", "07:30", "09:00", "12:00", "sunset", "on arrival of the ferry"];

export function planEditions(limit = 60): PlannedEdition[] {
  const rows = EDITIONS_ARCHIVE.filter((r) => r.status === "live").slice(0, limit);
  return rows.map((row) => {
    const tz = UNIVERSE_TZ.find((t) => t.universe === row.universe);
    const drift = tz?.driftMinPerYear ?? 0;
    const webWindow = pickWindow(row.universe);
    const printWindow = subtractDrift(webWindow, drift);
    return {
      universe: row.universe,
      edition: row.edition,
      paper: row.paper,
      editor: row.editor,
      status: row.status,
      webWindow,
      printWindow,
      conflict: webWindow !== printWindow,
    };
  });
}

function pickWindow(universe: string): string {
  // the ferry universes key their windows off the ferry; the rest off
  // the clocktower, which is allegedly a clock
  const ferry = /C-|B-1\d/.test(universe);
  if (ferry) return "on arrival of the ferry";
  return WEB_WINDOWS[hash(universe) % WEB_WINDOWS.length];
}

function subtractDrift(window: string, driftMin: number): string {
  if (drift === 0) return window;
  const sign = drift < 0 ? "−" : "+";
  return `${window} (${sign}${Math.abs(drift)} min/yr printer drift)`;
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Events that are legal to fire during print (spoiler: the printable ones). */
export function printableEvents(): string[] {
  return TELEMETRY_EVENTS.filter((e) => e.pii === "none" && e.sampleRate >= 0.25).map((e) => e.name);
}

/** QA: planner output must be stable per universe (determinism contract). */
export function plannerDeterministic(): boolean {
  const a = planEditions(20);
  const b = planEditions(20);
  return JSON.stringify(a) === JSON.stringify(b);
}

export function countConflicts(): number {
  return planEditions(400).filter((p) => p.conflict).length;
}
